import { type ReactNode } from 'react';
import { MemberAvatar } from '@/components/MemberAvatar';
import { projectAccent } from '@/lib/deterministic-color';
import { cn } from '@/lib/utils';

/**
 * Notes redesign — adapter shape consumed by `NoteCard` / `NoteRow` /
 * `NotePreviewPanel`. Pages map their `useWorkspaceNotes()` /
 * `useProjectNotes()` payload into this list so the rendering
 * components stay data-source agnostic.
 *
 * `parent` is the XOR project-vs-task pinning model; `projectSlug` /
 * `projectName` are populated only for project-parented notes (the
 * API doesn't expose the task's project today, so task-parented
 * notes have null project info — they bucket as "(no project)" in
 * the project group mode and use task-id-derived rail color).
 */
export interface NoteRecord {
  id: string;
  title: string;
  body: string;
  parent:
    | { kind: 'project'; projectSlug: string; projectName: string }
    | { kind: 'task'; taskId: string; taskTitle: string };
  /** Populated for project parents; null for task parents (see header). */
  projectSlug: string | null;
  projectName: string | null;
  /** Color seed for the rail — `projectSlug` for project parents,
   *  `taskId` for task parents. Hashed via `projectAccent` for an
   *  OKLCH hue that's stable across renders. */
  railSeed: string;
  authorId: string | null;
  authorName: string | null;
  authorEmail: string | null;
  /** Minutes since `updated_at` (or `created_at` if never updated).
   *  Used by recency grouping + "edited 5h ago" display. */
  recencyMinutes: number;
  /** True when `updated_at !== created_at` — flips the time label
   *  prefix from `5h ago` → `edited 5h ago`. */
  edited: boolean;
}

const fmtTimeAgo = (minutes: number): string => {
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${Math.round(minutes)}m ago`;
  if (minutes < 60 * 24) return `${Math.round(minutes / 60)}h ago`;
  const days = Math.round(minutes / (60 * 24));
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.round(days / 7)}w ago`;
  return `${Math.round(days / 30)}mo ago`;
};

/**
 * Highlight a `needle` substring inside `text` with a `<mark>` span.
 * Case-insensitive single-pass match (the in-page search is
 * "contains" semantics). Returns the original string when there's no
 * needle or no match — so callers can pass any text without paying
 * for unnecessary spans.
 */
export const highlightMatch = (text: string, needle: string): ReactNode => {
  if (!text || !needle) return text;
  const i = text.toLowerCase().indexOf(needle.toLowerCase());
  if (i === -1) return text;
  return (
    <>
      {text.slice(0, i)}
      <mark className="rounded-[2px] bg-[oklch(0.74_0.16_50/0.25)] px-px text-inherit">
        {text.slice(i, i + needle.length)}
      </mark>
      {text.slice(i + needle.length)}
    </>
  );
};

/**
 * Parent-chip — the "On project X" / "On task Y" pill that rides
 * above the title on cards (and inside the row body on rows).
 * Project parents get a rounded-square glyph; task parents get a
 * full circle, matching the prototype's `.is-task` modifier.
 */
export const ParentChip = ({ note }: { note: NoteRecord }) => {
  const color = projectAccent(note.railSeed);
  if (note.parent.kind === 'task') {
    return (
      <span
        title={`Pinned to task ${note.parent.taskTitle}`}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-[5px] border px-1.5 py-px',
          'border-[var(--glass-card-border)] bg-[var(--surface-quiet,var(--secondary))]',
          'text-foreground-muted text-[10.5px] font-medium',
          'max-w-[60%] overflow-hidden whitespace-nowrap',
        )}
      >
        <span
          aria-hidden="true"
          className="size-2 flex-shrink-0 rounded-full"
          style={{ background: color }}
        />
        <span className="truncate">{note.parent.taskTitle}</span>
      </span>
    );
  }
  return (
    <span
      title={`Pinned to ${note.parent.projectName}`}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-[5px] border px-1.5 py-px',
        'border-[var(--glass-card-border)] bg-[var(--surface-quiet,var(--secondary))]',
        'text-foreground-muted text-[10.5px] font-medium',
        'max-w-[60%] overflow-hidden whitespace-nowrap',
      )}
    >
      <span
        aria-hidden="true"
        className="size-2 flex-shrink-0 rounded-[2px]"
        style={{ background: color }}
      />
      <span className="truncate">{note.parent.projectName}</span>
    </span>
  );
};

export interface NoteCardProps {
  note: NoteRecord;
  /** Search needle for inline title/body highlighting. */
  search: string;
  onOpen: (note: NoteRecord) => void;
}

/**
 * Notes redesign — cards layout chip. Direct port of the prototype's
 * `.v1-note` styling: glass card with an absolute project-colored
 * rail on the left edge, parent chip + (deferred) pin slot on top,
 * 2-line clamped title, 3-line clamped body, footer with avatar +
 * author + relative time. Click opens the side preview panel via
 * `onOpen`.
 *
 * Pinning UI is deferred per Q5 — the layout reserves no space for a
 * pin button; when pinning ships it slots into the meta row's
 * trailing position.
 */
export const NoteCard = ({ note, search, onOpen }: NoteCardProps) => {
  const isUntitled = note.title.trim().length === 0;
  const isEmpty = note.body.trim().length === 0;
  const railColor = projectAccent(note.railSeed);

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={() => onOpen(note)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(note);
        }
      }}
      className={cn(
        'group relative flex min-h-[132px] cursor-pointer flex-col rounded-xl border p-[14px_16px_12px_18px] backdrop-blur-md',
        'border-[var(--glass-card-border)] bg-[var(--glass-card-bg)] shadow-[var(--glass-card-shadow)]',
        'transition-[border-color,transform,box-shadow] duration-150 ease-out',
        'hover:-translate-y-px hover:border-[var(--glass-card-hover-border)] hover:shadow-[var(--glass-card-hover-shadow)]',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--brand-accent)]',
      )}
    >
      <span
        aria-hidden="true"
        className="absolute top-[14px] bottom-[14px] left-0 w-[2px] rounded-r-full opacity-55"
        style={{ background: railColor }}
      />

      <div className="mb-2 flex min-h-[18px] flex-wrap items-center gap-2">
        <ParentChip note={note} />
        {/* Pin slot reserved (Q5 deferred); intentionally empty. */}
      </div>

      <h3
        className={cn(
          'm-0 mb-1.5 text-[14px] font-semibold tracking-[-0.01em] leading-[1.35] text-pretty',
          'overflow-hidden [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]',
          isUntitled
            ? 'text-foreground-faint italic font-medium'
            : 'text-foreground',
        )}
      >
        {isUntitled ? 'Untitled' : highlightMatch(note.title, search)}
      </h3>

      <p
        className={cn(
          'm-0 mb-2.5 flex-1 text-[12.5px] leading-[1.5] text-pretty',
          'overflow-hidden [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:3]',
          isEmpty ? 'text-foreground-faint italic' : 'text-foreground-muted',
        )}
      >
        {isEmpty ? 'No content yet.' : highlightMatch(stripHtml(note.body), search)}
      </p>

      <footer
        className={cn(
          'flex min-w-0 items-center gap-2 border-t pt-2',
          'border-[var(--glass-card-border)] text-foreground-faint text-[11px]',
        )}
      >
        <span className="inline-flex min-w-0 items-center gap-1.5">
          <MemberAvatar
            id={note.authorId}
            name={note.authorName}
            email={note.authorEmail}
            size="xs"
            className="!size-[18px] !text-[8px]"
          />
          <span className="text-foreground-muted truncate font-medium">
            {note.authorName ?? note.authorEmail ?? 'Unknown'}
          </span>
        </span>
        <span aria-hidden="true" className="opacity-50">·</span>
        <span
          className={cn(
            'whitespace-nowrap font-mono text-[10.5px] tabular-nums',
            note.edited && 'italic opacity-85',
          )}
        >
          {note.edited ? `edited ${fmtTimeAgo(note.recencyMinutes)}` : fmtTimeAgo(note.recencyMinutes)}
        </span>
      </footer>
    </article>
  );
};

/**
 * Strip HTML tags from a note body for plain-text preview rendering.
 * Note bodies are rich HTML (TipTap output); the card preview just
 * needs the text for the line-clamp + highlight match. Mirrors the
 * inline `stripHtmlForCheck` pattern used in the comment composer.
 */
export const stripHtml = (html: string): string =>
  html
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
